import {t} from './i18n.js';
import {recordings} from './audio-recordings.js';
import {prefetchAudio} from './audio-cache.js';
import {timestampFor,sentenceTarget} from './audio-position.js';
import {loadRecordingTimings} from './audio-timings.js';

export function parseCues(source){
  const clock=s=>{const parts=s.replace(',','.').split(':').map(Number);return parts.reduce((sum,n)=>sum*60+n,0);};
  const result=[];for(const block of source.replace(/\r/g,'').split(/\n\s*\n/)){
    const lines=block.trim().split('\n');const timing=lines.findIndex(line=>line.includes('-->'));if(timing<0)continue;
    const [start,end]=lines[timing].split('-->').map(s=>s.trim().split(/\s/)[0]);
    const location=timing>0?lines[timing-1].trim():'';
    const cue={start:clock(start),end:clock(end),location,text:lines.slice(timing+1).join(' ')};
    if(Number.isFinite(cue.start)&&Number.isFinite(cue.end)&&cue.end>cue.start)result.push(cue);
  }return result.sort((a,b)=>a.start-b.start);
}
export class AudioReader {
  constructor(engine,emit,persist){
    this.engine=engine;this.emit=emit;this.persist=persist;this.audio=new Audio();
    this.audio.preload='metadata';this.rate=1;this.voice='robo';this.active=false;
    this.cues=[];this.blocks=[];this.epoch=0;this.speaking=null;this.audioChapter=0;this.timingLoads=new Map();this.timingData=new Map();this.wordRequest=0;
    this.audio.addEventListener('timeupdate',()=>this.tick());
    this.audio.addEventListener('loadedmetadata',()=>{
      if(this.pendingPosition!=null){this.audio.currentTime=Math.min(this.audio.duration||this.pendingPosition,this.pendingPosition);this.pendingPosition=null;}
      this.applyReadingStart();this.audio.playbackRate=this.rate;this.state();
    });
    this.audio.addEventListener('play',()=>{this.active=true;this.mediaSession();this.state();this.prefetchChapters();});
    this.audio.addEventListener('ended',()=>{const next=this.nextRecordedChapter();if(next!==null)this.selectChapter(next,true).catch(error=>this.emit('error',error.message));else{this.save().catch(()=>{});this.state();}});
    for(const event of ['pause','ratechange'])this.audio.addEventListener(event,()=>this.state());
    this.audio.addEventListener('error',()=>{if(this.usesFile()&&this.active)emit('error',t("ui-this-audio-file-could-not-be-played-try-downloading"));});
  }
  recording(){return this.engine.book?.id==='easypeasy-en'?recordings[this.voice]:null;}
  chapterSource(index=this.audioChapter){const recording=this.recording(),chapter=this.engine.book?.chapters?.[index];return recording&&chapter&&!recording.unavailableChapters?.includes(chapter.id)?`${recording.directory}/${encodeURIComponent(chapter.id)}.mp3`:null;}
  nextRecordedChapter(){
    if(!this.recording())return null;
    for(let index=this.audioChapter+1;index<this.engine.book.chapters.length;index++)if(this.chapterSource(index))return index;
    return null;
  }
  clipKey(){return this.voice+':'+(this.engine.book?.chapters?.[this.audioChapter]?.id||'');}
  attach(saved){
    this.stop();this.navigationBlocks=null;this.readingStart=null;this.saved=saved||{};this.bookId=this.engine.book.id;
    this.cues=saved?.cues||[];
    if(this.objectUrl)URL.revokeObjectURL(this.objectUrl);
    this.objectUrl=saved?.blob?URL.createObjectURL(saved.blob):null;
    this.emit('audioLoaded',{bookId:this.engine.book.id,chapter:this.engine.capture()?.chapter||0,chapterCount:this.engine.book.chapters?.length||1,saved:{...this.saved,blob:undefined,hasFile:!!saved?.blob}});
    this.manifestPromise ||= fetch(new URL('audio/recordings.json',document.baseURI)).then(response=>{if(!response.ok)throw Error(t("ui-audio-manifest-unavailable"));return response.json();}).catch(()=>null);
    this.manifestPromise.then(manifest=>{this.manifest=manifest;this.state();});
  }
  configure({bookId,voice,chapter,rate,progress}){
    if(bookId!==this.engine.book?.id)return;
    this.voice=voice;this.audioChapter=chapter;this.rate=rate;this.progress=progress;
    this.configureFile();this.state();this.ensureTimings().catch(()=>{});
  }
  configureFile(){
    this.readingStart=null;
    const chapterSrc=this.chapterSource(),key=this.clipKey();
    const src=this.voice==='attached'?this.objectUrl:chapterSrc?new URL(chapterSrc,document.baseURI).href:null;
    this.pendingPosition=this.voice==='attached'?this.saved.position||0:this.saved.recordingFiles?.[key]===chapterSrc?this.saved.recordings?.[key]||0:0;
    if(src)this.audio.src=src;else this.audio.removeAttribute('src');
    this.audio.load();this.audio.playbackRate=this.rate;
  }
  usesFile(){return !!this.audio.src&&(this.voice==='attached'||!!this.recording());}
  canPlay(){return this.voice==='robo'||this.usesFile();}
  wholeRecording(){
    const metadata=this.recording()&&this.manifest?.recordings?.[this.voice];
    if(!metadata||metadata.chapters.length!==this.engine.book.chapters.length||!metadata.chapters.every((chapter,index)=>chapter.id===this.engine.book.chapters[index].id))return null;
    return metadata;
  }
  state(){
    const file=this.usesFile(),robo=this.voice==='robo';
    const playing=this.canPlay()&&(file?!this.audio.paused:this.playing===true);
    const position=file?this.audio.currentTime:robo?this.speechPosition||0:0;
    const duration=file?(Number.isFinite(this.audio.duration)?this.audio.duration:0):robo?this.speechDuration||0:0;
    const navigation=this.navigationTargets(),whole=this.wholeRecording();
    const totalDuration=whole?.duration||this.recording()?.runtime||duration;
    const totalPosition=position;
    const target=value=>!value?null:this.voice==='robo'?{kind:'speech',index:value.index,offset:value.offset}:{kind:'recording',chapter:value.chapter,seconds:value.seconds};
    const current=this.voice==='robo'&&this.blocks.length?{kind:'speech',index:Math.min(this.speechIndex??0,this.blocks.length-1),offset:this.spokenOffset??this.speechOffset??0}:this.recording()?{kind:'recording',chapter:this.audioChapter,seconds:this.audio.currentTime}:null;
    this.emit('audio',{hasProgress:!!this.progress,navigation:{timeline:whole?.chapters||[],recording:!!this.recording(),current,previousSentence:target(navigation.previousSentence),nextSentence:target(navigation.nextSentence),paragraphStart:target(navigation.paragraphStart)},totalPosition,totalSeekable:this.recording()?!!whole&&duration>0:duration>0,totalDuration,canPreviousSentence:!!navigation.previousSentence,canNextSentence:!!navigation.nextSentence,canParagraphStart:!!navigation.paragraphStart,playing,position,duration,rate:this.rate,active:this.active,voice:this.voice,available:this.canPlay(),placeholder:this.recording()?.placeholder===true,download:this.chapterSource()||'',chapter:this.audioChapter,chapterCount:this.recording()?this.engine.book.chapters.length:0,chapterTitle:this.recording()?this.engine.book.chapters[this.audioChapter]?.title||'':''});
    if('mediaSession'in navigator){
      navigator.mediaSession.playbackState=this.active?(playing?'playing':'paused'):'none';
      if(file&&duration>0)try{navigator.mediaSession.setPositionState?.({duration,position:Math.min(duration,Math.max(0,position)),playbackRate:this.rate});}catch{}
    }
  }
  recordingTimings(index=this.audioChapter){
    const recording=this.recording();if(!recording)return [];
    const chapter=this.engine.book.chapters[index],loaded=this.timingData.get(this.voice+':'+chapter?.id);
    return recording.timings?.[chapter?.id]||(loaded?.chapter===chapter?loaded.timings:[]);
  }
  async ensureTimings(index=this.audioChapter){
    const recording=this.recording(),chapter=this.engine.book?.chapters?.[index],voice=this.voice;
    if(recording?.timings?.[chapter?.id])return;
    if(!recording?.timingDirectory||!chapter||!this.chapterSource(index))return;
    const timings=await loadRecordingTimings(recording,chapter,document.baseURI,this.timingLoads);
    this.timingData.set(voice+':'+chapter.id,{chapter,timings});
    if(this.voice===voice&&this.engine.book.chapters[index]===chapter)this.state();
  }
  navigationTargets(){
    let blocks,index,offset;
    if(this.voice==='robo'){
      blocks=this.blocks;index=Math.min(this.speechIndex??0,blocks.length-1);offset=(this.speechIndex??0)>=blocks.length?(blocks[index]?.textContent.length||0):this.spokenOffset??this.speechOffset??0;
    }else if(this.recording()){
      const cue=this.recordingTimings().filter(cue=>cue.start<=this.audio.currentTime).sort((a,b)=>b.start-a.start)[0];
      if(!cue)return {};
      blocks=this.navigationBlocks||(this.navigationBlocks=this.engine.speakingBlocks());
      index=blocks.findIndex(block=>block.id===cue.block);offset=cue.offset||0;
    }else return {};
    if(!blocks?.length||index<0||!blocks[index])return {};
    const targets={previousSentence:sentenceTarget(blocks,index,offset,-1,this.engine.book.language),nextSentence:sentenceTarget(blocks,index,offset,1,this.engine.book.language),paragraphStart:{index,offset:0}};
    for(const [action,target]of Object.entries(targets)){
      if(!target)continue;
      target.block=blocks[target.index];
      if(this.recording()){
        const chapter=target.block.closest('.book-chapter');
        target.chapter=Number(chapter?.dataset.index??this.audioChapter);
        const timings=this.recordingTimings(target.chapter);
        target.seconds=timings.find(cue=>cue.block===target.block.id&&(cue.offset||0)===target.offset)?.start;
        if(!Number.isFinite(target.seconds))targets[action]=null;
      }
    }
    return targets;
  }
  async navigateTo(target){
    if(!target||(target.kind==='speech')!==(this.voice==='robo'))return;
    if(target.kind==='speech'){target={...target,block:this.blocks[target.index]};if(!target.block)return;}
    this.progress=true;this.saved.started=true;
    if(this.voice==='robo'){
      const playing=this.playing===true;this.epoch++;speechSynthesis.cancel();this.speaking?.classList.remove('speaking');this.speaking=null;
      this.speechIndex=target.index;this.speechOffset=target.offset;this.spokenOffset=target.offset;
      this.speechPosition=this.durations.slice(0,target.index).reduce((sum,duration)=>sum+duration,0)+this.durations[target.index]*target.offset/Math.max(1,target.block.textContent.length);
      if(playing){speechSynthesis.resume();this.speak(this.epoch);}else this.state();
    }else{
      const playing=!this.audio.paused;
      if(target.chapter!==this.audioChapter){await this.selectChapter(target.chapter,false,true,true);this.readingStart={seconds:target.seconds};this.applyReadingStart();if(playing)await this.audio.play();}
      else await this.seek(target.seconds,{keepReturn:true});
      this.state();
    }
    await this.save();
  }
  async selectVoice(voice){
    if(!['hackauthor','hackauthor-no-breaks','john-lothe','ai-girlfriend','robo'].includes(voice)||voice===this.voice)return;
    await this.save();this.stop();this.voice=voice;this.saved={...this.saved,voice,audioChapters:{...this.saved.audioChapters,[voice]:this.audioChapter}};
    this.configureFile();await this.prepare();await this.save();
  }
  async selectChapter(index,play=this.usesFile()&&!this.audio.paused,follow=true,preserveNavigation=false){
    if(!this.recording()||index<0||index>=this.engine.book.chapters.length)return;
    this.progress=true;this.saved.started=true;const active=this.active;await this.save();this.stop(preserveNavigation);this.audioChapter=index;this.saved={...this.saved,audioChapters:{...this.saved.audioChapters,[this.voice]:index}};this.configureFile();this.active=active;
    if(follow&&this.engine.settings.follow)this.engine.chapter(index);
    await this.ensureTimings().catch(()=>{});
    this.state();if(play){this.active=true;await this.audio.play();}await this.save();
  }
  prefetchChapters(){
    if(!this.recording()||!navigator.serviceWorker?.controller)return;
    const key=this.clipKey();if(this.prefetchKey===key)return;
    this.prefetchKey=key;this.prefetchController?.abort();this.prefetchController=new AbortController();
    const available=this.engine.book.chapters.map((_,index)=>index).filter(index=>this.chapterSource(index));
    this.emit('audioPrefetch',{available,chapter:this.audioChapter});
  }
  prefetch({next,keep}){
    if(!this.recording())return;
    const urls=indexes=>indexes.map(index=>this.chapterSource(index)).filter(Boolean).map(source=>new URL(source,document.baseURI).href);
    this.prefetchController?.abort();this.prefetchController=new AbortController();
    prefetchAudio(urls(next),urls(keep),this.prefetchController.signal).catch(()=>{});
  }
  prepareSpeech(target=this.engine.listeningTarget()){
    this.blocks=this.engine.speakingBlocks();this.speechIndex=Math.max(0,this.blocks.indexOf(target?.block));this.speechOffset=target?.offset||0;this.spokenOffset=this.speechOffset;
    this.durations=this.blocks.map(b=>Math.max(1,b.textContent.trim().split(/\s+/).length/3.3));this.speechDuration=this.durations.reduce((a,b)=>a+b,0);
    this.speechPosition=this.durations.slice(0,this.speechIndex).reduce((a,b)=>a+b,0)+this.durations[this.speechIndex]*this.speechOffset/Math.max(1,this.blocks[this.speechIndex]?.textContent.length||1);
  }
  async prepare(fromViewport=false){
    const target=fromViewport&&!this.progress?this.engine.listeningTarget():null,epoch=this.epoch;
    await this.ensureTimings(target?.chapter??this.audioChapter).catch(()=>{});
    if(epoch!==this.epoch)return;
    if(target&&!this.progress)this.startFromReading(target);
    else if(this.voice==='robo'&&!this.blocks.length)this.prepareSpeech();
    this.state();
  }
  startFromReading(target){
    if(!target)return;
    if(this.voice==='robo'){this.prepareSpeech(target);return;}
    if(this.recording()){
      if(this.audioChapter!==target.chapter){this.audioChapter=target.chapter;this.configureFile();}
      const timings=this.recordingTimings(target.chapter);
      this.readingStart={fraction:target.fraction,seconds:timestampFor(target,timings)??(this.recording().timingDirectory?0:undefined)};
    }else if(this.voice==='attached'){
      const cue=this.cues.find(c=>{try{const location=JSON.parse(c.location);return location.block===target.blockId;}catch{return c.location===target.blockId;}});
      if(!cue){this.emit('toast',t("ui-add-audiobook-timestamps-to-sync-this-recording-with-the"));return;}
      this.readingStart={seconds:cue.start};
    }
    this.pendingPosition=null;this.applyReadingStart();this.state();
  }
  applyReadingStart(){
    if(!this.readingStart||!Number.isFinite(this.audio.duration))return;
    const {seconds,fraction}=this.readingStart;
    this.audio.currentTime=Math.min(Math.max(0,seconds??fraction*this.audio.duration),Math.max(0,this.audio.duration-.25));
    this.readingStart=null;
  }
  async startAtWord(target){
    const epoch=this.epoch,request=++this.wordRequest;
    if(this.recording()?.timingDirectory){
      if(!this.chapterSource(target.chapter)){this.emit('toast',t("ui-this-narrator-does-not-read-this-chapter-choose-the"));return;}
      await this.ensureTimings(target.chapter);
      if(epoch!==this.epoch||request!==this.wordRequest)return;
      if(timestampFor(target,this.recordingTimings(target.chapter))===undefined){this.emit('toast',t("ui-this-passage-is-not-matched-to-the-recording-choose"));return;}
    }
    this.stop();this.startFromReading(target);await this.toggle();
    if(this.recording()&&!this.recording()?.timingDirectory&&timestampFor(target,this.recordingTimings(target.chapter))===undefined)this.emit('toast',t("ui-starting-near-this-word-recording-timestamps-are-not-available"));
  }
  async toggleInline(fresh){
    if(fresh&&!this.progress){
      const target=this.engine.inlineAudioTarget(),epoch=this.epoch;
      await this.ensureTimings(target?.chapter??this.audioChapter).catch(()=>{});
      if(epoch!==this.epoch)return;
      if(!this.progress)this.startFromReading(target);
    }
    return this.toggle();
  }
  async toggle(){
    if(!this.canPlay())return;
    this.active=true;this.progress=true;this.saved.started=true;
    if(this.usesFile()){
      if(this.audio.paused)await this.audio.play();else this.audio.pause();
      this.state();return;
    }
    if(!('speechSynthesis'in window))throw Error(t("ui-this-browser-does-not-support-spoken-text-attach-an"));
    if(this.playing){speechSynthesis.pause();this.playing=false;this.state();return;}
    if(speechSynthesis.paused&&this.speaking&&this.blocks.length){speechSynthesis.resume();this.playing=true;this.state();return;}
    await this.startSpeech();
  }
  async startSpeech(){if(!this.blocks.length)this.prepareSpeech();if(!this.blocks.length)throw Error(t("ui-there-is-no-readable-text-here-yet-for-a"));this.playing=true;this.epoch++;speechSynthesis.resume();this.speak(this.epoch);}
  speak(epoch){if(epoch!==this.epoch||!this.playing)return;if(this.speechIndex>=this.blocks.length){this.playing=false;this.state();return;}const block=this.blocks[this.speechIndex];const offset=this.speechOffset||0;const utterance=new SpeechSynthesisUtterance(block.textContent.slice(offset));utterance.lang=this.engine.book.language || 'en';utterance.rate=this.rate;const voices=speechSynthesis.getVoices();utterance.voice=voices.find(v=>v.lang.toLowerCase()===utterance.lang.toLowerCase())||voices.find(v=>v.lang.toLowerCase().startsWith(utterance.lang.split('-')[0]))||null;this.speaking=block;this.spokenOffset=offset;block.classList.add('speaking');this.speechPosition=this.durations.slice(0,this.speechIndex).reduce((a,b)=>a+b,0)+offset/Math.max(1,block.textContent.length)*this.durations[this.speechIndex];
    if(this.engine.settings.follow){if(this.engine.epub)block.scrollIntoView({block:'center',behavior:'smooth'});else if(this.engine.settings.mode==='tap')this.engine.go({block:block.id,chapterId:block.closest('.book-chapter')?.id});else block.scrollIntoView({block:'center',behavior:this.engine.settings.reduced?'instant':'smooth'});}
    utterance.onboundary=e=>{if(epoch!==this.epoch)return;this.spokenOffset=offset+e.charIndex;this.speechPosition=this.durations.slice(0,this.speechIndex).reduce((a,b)=>a+b,0)+(offset+e.charIndex)/Math.max(1,block.textContent.length)*this.durations[this.speechIndex];this.state();};
    utterance.onend=()=>{block.classList.remove('speaking');if(epoch!==this.epoch)return;this.speechIndex++;this.speechOffset=0;this.speak(epoch);};
    utterance.onerror=e=>{if(['canceled','interrupted'].includes(e.error))return;this.playing=false;this.emit('error',t("ui-spoken-text-is-unavailable-with-this-devices-current-voices"));this.state();};
    speechSynthesis.speak(utterance);this.state();this.mediaSession();
  }
  stop(preserveNavigation=false){
    if(!preserveNavigation)this.emit('audioReset');
    this.epoch++;this.prefetchController?.abort();this.prefetchKey=null;this.audio.pause();if('speechSynthesis'in window)speechSynthesis.cancel();
    this.playing=false;this.active=false;this.speaking?.classList.remove('speaking');
    this.blocks=[];this.speechOffset=0;this.spokenOffset=0;this.readingStart=null;this.durations=[];this.speechPosition=0;this.speechDuration=0;this.speaking=null;
    clearTimeout(this.saveTimer);this.saveTimer=null;this.state();
  }
  async setRate(rate){
    this.rate=Math.max(.5,Math.min(3,Number(rate)||1));this.audio.playbackRate=this.rate;
    if(this.playing&&!this.usesFile()){this.epoch++;speechSynthesis.cancel();this.speak(this.epoch);}
    this.state();await this.save();
  }
  async seekRecording(target){
    if(!this.recording()||target.kind!=='recording')return;
    this.emit('audioReset');const playing=!this.audio.paused;this.active=true;
    if(target.chapter!==this.audioChapter){
      await this.selectChapter(target.chapter,false,false);
      if(this.audio.readyState>0)await this.seek(target.seconds);
      else{this.pendingPosition=target.seconds;this.state();}
      if(playing)await this.audio.play();await this.save();
    }else await this.seek(target.seconds);
  }
  async seek(seconds,{keepReturn=false}={}){
    if(!keepReturn)this.emit('audioReset');
    this.progress=true;this.saved.started=true;this.readingStart=null;this.pendingPosition=null;
    seconds=Math.max(0,Number(seconds)||0);
    if(this.usesFile()){
      this.audio.currentTime=Math.min(Number.isFinite(this.audio.duration)?this.audio.duration:seconds,seconds);
      await this.save();
    }else if(this.voice==='robo'){
      if(!this.blocks.length)this.prepareSpeech();
      if(this.blocks.length){let sum=0,index=0;while(index<this.durations.length-1&&sum+this.durations[index]<seconds)sum+=this.durations[index++];this.speechIndex=index;this.speechOffset=0;this.spokenOffset=0;this.speechPosition=sum;this.epoch++;speechSynthesis.cancel();this.speaking?.classList.remove('speaking');this.speaking=null;if(this.playing){speechSynthesis.resume();this.speak(this.epoch);}}
    }
    this.state();
  }
  tick(){
    if(!this.usesFile())return;
    if(!this.audio.paused)this.prefetchChapters();
    const cue=this.voice==='attached'?this.cues.find(c=>c.start<=this.audio.currentTime&&c.end>this.audio.currentTime):null;
    if(cue&&cue!==this.lastCue){this.lastCue=cue;if(this.engine.settings.follow&&cue.location){let location;try{location=JSON.parse(cue.location);}catch{location={kind:'flow',block:cue.location};}this.engine.go(location);}}
    this.state();
    // Save steadily during background playback rather than waiting for a gap in timeupdate events.
    if(!this.saveTimer)this.saveTimer=setTimeout(()=>{this.saveTimer=null;this.save().catch(error=>this.emit('error',error.message));},1000);
  }
  async save(){
    if(!this.saved||this.bookId!==this.engine.book?.id)return;
    const published=this.recording()&&this.usesFile()&&this.audio.readyState>0;
    this.saved={...this.saved,position:this.voice==='attached'&&this.usesFile()?this.audio.currentTime:this.saved.position||0,rate:this.rate,voice:this.voice,
      ...(published?{recordings:{...this.saved.recordings,[this.clipKey()]:this.audio.currentTime},recordingFiles:{...this.saved.recordingFiles,[this.clipKey()]:this.chapterSource()},audioChapters:{...this.saved.audioChapters,[this.voice]:this.audioChapter}}:{})};
    await this.persist(this.saved,this.bookId);
  }
  mediaSession(){
    if(!('mediaSession'in navigator))return;
    navigator.mediaSession.metadata=new MediaMetadata({title:this.engine.book.title,artist:this.recording()?.name||this.engine.book.author||''});
    navigator.mediaSession.setActionHandler('play',()=>{if(this.usesFile()&&this.audio.paused||!this.usesFile()&&!this.playing)this.toggle().catch(error=>this.emit('error',error.message));});
    navigator.mediaSession.setActionHandler('pause',()=>{if(this.usesFile()&&!this.audio.paused||!this.usesFile()&&this.playing)this.toggle().catch(error=>this.emit('error',error.message));});
    navigator.mediaSession.setActionHandler('seekforward',()=>this.emit('audioNavigate','forward'));
    navigator.mediaSession.setActionHandler('seekbackward',()=>this.emit('audioNavigate','previousSentence'));
    navigator.mediaSession.setActionHandler('seekto',event=>this.emit('audioSeek',event.seekTime));
  }
}
